import { NotificationType } from '../enums';
import {
  isAnyNotificationChannelEnabled,
  isInAppEnabled,
  parseNotificationPrefs,
  withNotificationChannels,
} from './notification-prefs';

export type PreparedNotificationDispatch = {
  notification: {
    userId: string;
    type: NotificationType;
    payload: Record<string, unknown>;
  };
  outbox: {
    eventType: 'side_effects';
    payload: {
      userId: string;
      type: NotificationType;
      payload: Record<string, unknown>;
      /** Filled by persistence layer after notification insert. */
      notificationId?: string;
    };
  };
};

/**
 * Shared notification + outbox preparation for API and worker dispatch paths.
 * Returns null when all channels are disabled for the type.
 */
export function prepareNotificationDispatch(
  userId: string,
  type: NotificationType,
  payload: Record<string, unknown>,
  notificationPrefsRaw: unknown,
): PreparedNotificationDispatch | null {
  const accountCritical =
    type === NotificationType.MODERATION_ACTION ||
    type === NotificationType.SUBSCRIPTION_UPDATED ||
    type === NotificationType.COMMUNITY_MOD_ACTION ||
    type === NotificationType.COMMUNITY_BILLING ||
    type === NotificationType.COURSE_PURCHASE_RECEIPT;

  if (accountCritical) {
    const enrichedPayload = withNotificationChannels(payload, { inApp: true });
    return {
      notification: { userId, type, payload: enrichedPayload },
      outbox: {
        eventType: 'side_effects',
        payload: { userId, type, payload: enrichedPayload },
      },
    };
  }

  const prefs = parseNotificationPrefs(notificationPrefsRaw);
  if (!isAnyNotificationChannelEnabled(prefs, type)) return null;

  const inApp = isInAppEnabled(prefs, type);
  const enrichedPayload = withNotificationChannels(payload, { inApp });

  return {
    notification: {
      userId,
      type,
      payload: enrichedPayload,
    },
    outbox: {
      eventType: 'side_effects',
      payload: {
        userId,
        type,
        payload: enrichedPayload,
      },
    },
  };
}

export const MARK_NOTIFICATIONS_READ_MAX_IDS = 100;
